import React from "react";
import { Box, Card, CardContent, CircularProgress, Stack, Typography } from "@mui/material";
import {
  Bar, BarChart, CartesianGrid, Legend, Line, LineChart, ResponsiveContainer,
  Tooltip as RechartsTooltip, XAxis, YAxis,
} from "recharts";
import { useTranslation } from "react-i18next";
import { formatMetric, useAnalyticsOverview } from "../../../api/analytics";

interface Props {
  fleetId: number;
  from: string;
  to: string;
}

const KPI_KEYS = [
  { key: "tco", label: "TCO total" },
  { key: "cost_per_km", label: "Cout au km" },
  { key: "fuel_cost", label: "Carburant / energie" },
  { key: "maintenance_cost", label: "Maintenance" },
  { key: "tax_cost", label: "Fiscalite" },
  { key: "km_driven", label: "Kilometres" },
  { key: "co2_total", label: "CO2" },
  { key: "vehicle_count", label: "Vehicules" },
];

/**
 * Fleet overview.
 *
 * Every KPI carries its variation against the equivalent previous period. A
 * standalone number tells a fleet manager nothing actionable; "maintenance up
 * 34% versus last quarter" is what makes them open the ranking.
 *
 * Formatting always uses the unit the SERVER declared for the metric. Guessing
 * from the metric name is how a cost in cents ends up rendered as kilometres.
 */
export const Vue: React.FC<Props> = ({ fleetId, from, to }) => {
  const { t } = useTranslation();
  const { overview, isLoading } = useAnalyticsOverview(fleetId, from, to);

  if (isLoading) {
    return <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}><CircularProgress /></Box>;
  }

  const totals = overview?.totals ?? {};
  const previous = overview?.previous ?? null;
  const units: Record<string, string> = overview?.units ?? {};

  const variation = (key: string): number | null => {
    if (!previous) return null;
    const now = Number(totals[key] ?? 0);
    const before = Number(previous[key] ?? 0);
    if (!before) return null;
    return ((now - before) / before) * 100;
  };

  const monthly = (overview?.monthly ?? []).map((row: any) => ({
    month: row.month,
    tco: Number(row.tco ?? 0) / 100,
    fuel: Number(row.fuel_cost ?? 0) / 100,
    maintenance: Number(row.maintenance_cost ?? 0) / 100,
    km: Number(row.km_driven ?? 0),
  }));

  const byEnergy = (overview?.by_energy ?? []).map((row: any) => ({
    energy: row.energy ?? "UNKNOWN",
    tco: Number(row.tco ?? 0) / 100,
    vehicles: Number(row.vehicle_count ?? 0),
  }));

  return (
    <Stack spacing={3}>
      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr 1fr", md: "repeat(4, 1fr)" },
        }}
      >
        {KPI_KEYS.map((kpi) => {
          const delta = variation(kpi.key);
          // Lower is better for cost metrics; the colour must not congratulate
          // a customer whose fuel bill just went up.
          const isCost = kpi.key !== "km_driven" && kpi.key !== "vehicle_count";
          const good = delta === null ? null : isCost ? delta < 0 : delta > 0;

          return (
            <Card key={kpi.key}>
              <CardContent>
                <Typography variant="caption" color="text.secondary">{kpi.label}</Typography>
                <Typography variant="h6" fontWeight={700}>
                  {formatMetric(totals[kpi.key] ?? null, units[kpi.key])}
                </Typography>
                {delta !== null && (
                  <Typography
                    variant="caption"
                    sx={{ color: good ? "success.main" : "error.main" }}
                  >
                    {delta > 0 ? "+" : ""}{delta.toFixed(1)}% {t("analytics.vsPrevious", "vs periode precedente")}
                  </Typography>
                )}
              </CardContent>
            </Card>
          );
        })}
      </Box>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("analytics.monthlyTrend", "Evolution mensuelle (EUR)")}
          </Typography>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <RechartsTooltip />
              <Legend />
              <Line type="monotone" dataKey="tco" name="TCO" strokeWidth={2} />
              <Line type="monotone" dataKey="fuel" name="Carburant" strokeWidth={1} />
              <Line type="monotone" dataKey="maintenance" name="Maintenance" strokeWidth={1} />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            {t("analytics.byEnergy", "TCO par energie")}
          </Typography>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={byEnergy}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="energy" />
              <YAxis />
              <RechartsTooltip />
              <Legend />
              <Bar dataKey="tco" name="TCO (EUR)" />
              <Bar dataKey="vehicles" name="Vehicules" />
            </BarChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>
    </Stack>
  );
};
